// @ts-strict-ignore
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, Customer, Employee, User } from '@fc-core';
import { getCurrentCustomer } from '@fc-store/selectors/current-customer.selector';
import { getCurrentUser } from '@fc-store/selectors/current-user.selector';
import { getCurrentEmployee } from '@fc-store/selectors/employee.selectors';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface HubSpotVisitorContext {
  companyId?: string;
  companyName?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
}

type HubSpotQueueCommand = ['identify', Record<string, string>];
type HubSpotPageCommand = ['setPath', string] | ['trackPageView'];
type HubSpotQueue = Array<HubSpotQueueCommand | HubSpotPageCommand>;

interface HubSpotWidgetStatus {
  loaded: boolean;
}

interface HubSpotWidgetApi {
  load(options?: { widgetOpen?: boolean }): void;
  refresh(options?: { openToNewThread?: boolean }): void;
  remove(): void;
  status(): HubSpotWidgetStatus;
  open(): void;
  close(): void;
}

interface HubSpotApi {
  clear(): void;
  widget: HubSpotWidgetApi;
  on(eventName: string, callback: (payload?: any) => void): void;
  off(eventName: string, callback: (payload?: any) => void): void;
}

interface HubSpotConversationsSettings {
  identificationEmail?: string;
  identificationToken?: string;
  loadImmediately?: boolean;
}

declare global {
  interface Window {
    HubSpotConversations?: HubSpotApi;
    _hsq?: HubSpotQueue;
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: HubSpotConversationsSettings;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly subscriptions = new Subscription();
  private currentVisitorContext: HubSpotVisitorContext | null = null;
  private hubSpotInitialized = false;
  private hubSpotOpenRequested = false;
  private hubSpotReady = false;
  private hubSpotCloseHandlerBound = false;
  private visitorIdentityKey: string | null = null;

  initGoogleAnalytics(): void {
    const gaScript = document.createElement('script');
    gaScript.setAttribute('async', 'true');
    gaScript.setAttribute(
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsApiKey}`,
    );
    const gaScript2 = document.createElement('script');
    gaScript2.innerText = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${environment.googleAnalyticsApiKey}\');`;
    document.documentElement.firstChild.appendChild(gaScript);
    document.documentElement.firstChild.appendChild(gaScript2);
  }

  pushToDataLayer(data: any): void {
    (window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
    (window as any)['dataLayer'].push(data);
  }

  setUpConfig(): void {
    this.gtag('config', environment.googleAnalyticsApiKey);
  }

  setAppVersion(): void {
    this.pushToDataLayer({
      event: 'web_release',
      value: (document.querySelector('meta[name="app-version"]') as any)[
        'content'
      ],
    });
  }

  initHubSpotChat(): void {
    if (!environment.hubspotHubId || this.hubSpotInitialized) {
      return;
    }

    this.hubSpotInitialized = true;
    window._hsq = window._hsq || [];
    window.hsConversationsSettings = {
      ...(window.hsConversationsSettings || {}),
      loadImmediately: false,
    };

    this.pushHubSpotPath(this.router.url);
    this.registerHubSpotReadyHandler();
    this.bindHubSpotRouteTracking();
    this.bindHubSpotVisitorTracking();

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = `https://js.hs-scripts.com/${environment.hubspotHubId}.js`;
    script.onerror = () => {
      console.warn('Failed to load HubSpot chat widget');
    };
    document.head.appendChild(script);
  }

  openHubSpotChat(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      if (!environment.hubspotHubId) {
        console.warn('HubSpot chat is not configured');
        return;
      }

      this.hubSpotOpenRequested = true;

      if (!this.hubSpotInitialized) {
        this.initHubSpotChat();
      }

      if (this.openHubSpotWidget()) {
        return;
      }
    } catch (error) {
      console.log('Error opening HubSpot chat', error);
    }
  }

  get gtag() {
    return (window as any)['gtag'];
  }

  private bindHubSpotRouteTracking(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
          map((event) => event.urlAfterRedirects),
        )
        .subscribe((path) => {
          this.pushHubSpotPath(path);

          if (this.hubSpotReady) {
            this.trackHubSpotPageView();
          }
        }),
    );
  }

  private bindHubSpotVisitorTracking(): void {
    this.subscriptions.add(
      combineLatest([
        this.auth.loggedIn$(),
        this.store.select(getCurrentUser),
        this.store.select(getCurrentEmployee),
        this.store.select(getCurrentCustomer),
      ])
        .pipe(
          map(([isLoggedIn, currentUser, currentEmployee, currentCustomer]) =>
            this.buildVisitorContext(
              isLoggedIn,
              currentUser,
              currentEmployee,
              currentCustomer,
            ),
          ),
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          }),
        )
        .subscribe((context) => this.syncHubSpotVisitorContext(context)),
    );
  }

  private buildVisitorContext(
    isLoggedIn: boolean,
    currentUser: User | null,
    currentEmployee: Employee | null,
    currentCustomer: Customer | null,
  ): HubSpotVisitorContext | null {
    if (!isLoggedIn) {
      return null;
    }

    const email =
      currentEmployee?.email ||
      currentEmployee?.user?.email ||
      currentUser?.email ||
      this.auth.tokenData?.email;

    if (!email) {
      return null;
    }

    return {
      email,
      firstName: currentEmployee?.firstName,
      lastName: currentEmployee?.lastName,
      userId: currentUser?.id || this.auth.tokenData?.user_id,
      companyId: currentCustomer?.id || this.auth.tokenData?.customer_id,
      companyName: currentCustomer?.name,
    };
  }

  private syncHubSpotVisitorContext(
    context: HubSpotVisitorContext | null,
  ): void {
    const nextIdentityKey = context
      ? `${context.email}:${context.userId || ''}`
      : null;

    if (this.visitorIdentityKey !== nextIdentityKey) {
      this.resetHubSpotConversation();
    }

    this.visitorIdentityKey = nextIdentityKey;
    this.currentVisitorContext = context;

    if (context) {
      this.identifyHubSpotVisitor(context);
    }
  }

  private identifyHubSpotVisitor(context: HubSpotVisitorContext): void {
    const identifyPayload: Record<string, string> = {
      email: context.email,
    };

    if (context.firstName) {
      identifyPayload.firstname = context.firstName;
    }

    if (context.lastName) {
      identifyPayload.lastname = context.lastName;
    }

    if (context.userId) {
      identifyPayload.user_id = context.userId;
    }

    if (context.companyId) {
      identifyPayload.company_id = context.companyId;
    }

    if (context.companyName) {
      identifyPayload.company = context.companyName;
      identifyPayload.company_name = context.companyName;
    }

    window._hsq = window._hsq || [];
    window._hsq.push(['identify', identifyPayload]);

    if (this.hubSpotReady) {
      this.trackHubSpotPageView();
    }
  }

  private pushHubSpotPath(path: string): void {
    if (!path) {
      return;
    }

    window._hsq = window._hsq || [];
    window._hsq.push(['setPath', path]);
  }

  private trackHubSpotPageView(): void {
    window._hsq = window._hsq || [];
    window._hsq.push(['trackPageView']);
  }

  private registerHubSpotReadyHandler(): void {
    const onReady = () => {
      this.hubSpotReady = true;
      this.bindHubSpotCloseHandler();

      if (this.currentVisitorContext) {
        this.identifyHubSpotVisitor(this.currentVisitorContext);
      }

      // Only load/open the widget if the user explicitly asked for it (via the
      // Help center "Live chat" action). We never load it proactively, so the
      // default floating launcher button never appears on its own.
      if (this.hubSpotOpenRequested) {
        this.ensureHubSpotWidgetLoaded({ widgetOpen: true });
      }
    };

    if (window.HubSpotConversations) {
      onReady();
      return;
    }

    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(onReady);
  }

  private bindHubSpotCloseHandler(): void {
    if (this.hubSpotCloseHandlerBound || !window.HubSpotConversations?.on) {
      return;
    }

    this.hubSpotCloseHandlerBound = true;

    // When the user closes the chat, remove the widget entirely so the floating
    // launcher button goes away. The chat can only be reopened through the Help
    // center "Live chat" action.
    window.HubSpotConversations.on('widgetClosed', () => {
      window.HubSpotConversations?.widget.remove();
    });
  }

  private ensureHubSpotWidgetLoaded(options?: { widgetOpen?: boolean }): void {
    if (!this.hubSpotReady || !window.HubSpotConversations?.widget) {
      return;
    }

    const { widget } = window.HubSpotConversations;
    const status = widget.status();

    if (status.loaded) {
      if (options?.widgetOpen) {
        widget.open();
        this.hubSpotOpenRequested = false;
      } else {
        widget.refresh();
      }
      return;
    }

    if (options?.widgetOpen) {
      widget.load({ widgetOpen: true });
      this.hubSpotOpenRequested = false;
      return;
    }

    widget.load();
  }

  private openHubSpotWidget(): boolean {
    if (!this.hubSpotReady || !window.HubSpotConversations?.widget) {
      return false;
    }

    this.ensureHubSpotWidgetLoaded({ widgetOpen: true });
    return true;
  }

  private resetHubSpotConversation(): void {
    if (!this.hubSpotReady || !window.HubSpotConversations) {
      return;
    }

    window.HubSpotConversations.clear();
    window.HubSpotConversations.widget.remove();
  }
}
